import React from 'react';

/*
 * ModuleIcon — distinct, term-appropriate section-header icons.
 *
 * The dashboard modules previously all reused the same S3 image
 * (external-attack-surface-scan.svg). This renders a self-contained inline
 * SVG per module so each section (Assets Inventory, Score Factors, TPCRM, …)
 * has a glyph that matches its meaning. No external dependency / no S3 upload.
 *
 * Usage: <ModuleIcon name="assets" /> — see ICONS below for available names.
 */

const ICONS = {
    // NOVA AI — cpu / intelligence
    nova: (
        <>
            <rect x="4" y="4" width="16" height="16" rx="2" />
            <rect x="9" y="9" width="6" height="6" />
            <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
            <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
            <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" />
            <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" />
        </>
    ),
    // EASM / External Attack Surface — globe
    easm: (
        <>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </>
    ),
    // Score Factors — bar chart / metrics
    score: (
        <>
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
        </>
    ),
    // Assets Inventory — server / stacked assets
    assets: (
        <>
            <rect x="2" y="2" width="20" height="8" rx="2" />
            <rect x="2" y="14" width="20" height="8" rx="2" />
            <line x1="6" y1="6" x2="6.01" y2="6" />
            <line x1="6" y1="18" x2="6.01" y2="18" />
        </>
    ),
    // TPCRM / Third-Party Risk — people / network
    tpcrm: (
        <>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
    ),
    // Compliance — shield / assurance
    compliance: (
        <>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
        </>
    ),
    // Audit Platform / Internal Audit — clipboard checklist
    audit: (
        <>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <rect x="8" y="2" width="8" height="4" rx="1" />
            <path d="M9 14l2 2 4-4" />
        </>
    ),
    // Risk Registry — alert triangle
    risk: (
        <>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
    ),
    // Vulnerability & Patch Management — bug
    vulnerability: (
        <>
            <path d="M8 7a4 4 0 0 1 8 0" />
            <rect x="7" y="8" width="10" height="11" rx="5" />
            <line x1="12" y1="8" x2="12" y2="19" />
            <line x1="2" y1="11" x2="7" y2="12" /><line x1="22" y1="11" x2="17" y2="12" />
            <line x1="2" y1="17" x2="7" y2="16" /><line x1="22" y1="17" x2="17" y2="16" />
            <line x1="4" y1="6" x2="7.5" y2="9" /><line x1="20" y1="6" x2="16.5" y2="9" />
        </>
    ),
    // Policy Center — document
    policy: (
        <>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </>
    ),
    // Add-on Services — grid of modules
    addons: (
        <>
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
        </>
    ),
    // Integration Cloud — cloud
    integration: (
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    ),
    // Organization Questions — help circle
    questions: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
    ),

    // ── AI Adoption Portal nav glyphs ─────────────────────────────
    // Executive Dashboard — layout / panels
    dashboard: (
        <>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
        </>
    ),
    // Adoption Posture — target / coverage rings
    coverage: (
        <>
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </>
    ),
    // Detection Signatures — fingerprint
    fingerprint: (
        <>
            <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
            <path d="M8.5 8.5A5 5 0 0 1 17 12c0 1.5 0 3-.5 4.5" />
            <path d="M5.5 11a7 7 0 0 1 13 1c0 2-.2 4-.7 6" />
            <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
            <path d="M2 16h.01" />
        </>
    ),
    // Group Enforcement — users + check
    users: (
        <>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
        </>
    ),
    // Endpoint Enforcement — monitor / screen
    endpoint: (
        <>
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
        </>
    ),
    // LLM Gateway — entry / log-in door
    gateway: (
        <>
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
        </>
    ),
    // Allowlist — check circle
    allowlist: (
        <>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </>
    ),
    // Policy Violations — alert octagon
    violation: (
        <>
            <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </>
    ),
    // AI Budget — dollar sign
    budget: (
        <>
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </>
    ),
    // Cost Advisor — lightbulb / advice
    lightbulb: (
        <>
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.76.76 1.23 1.52 1.41 2.5" />
        </>
    ),
    // Frameworks — layers / stack
    layers: (
        <>
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
        </>
    ),
    // Evidence Ledger — book / ledger
    ledger: (
        <>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </>
    ),
    // AI Policy Generator — magic wand
    wand: (
        <>
            <path d="M15 4V2" />
            <path d="M15 16v-2" />
            <path d="M8 9h2" />
            <path d="M20 9h2" />
            <path d="M17.8 11.8L19 13" />
            <path d="M17.8 6.2L19 5" />
            <path d="M3 21l9-9" />
            <path d="M12.2 6.2L11 5" />
        </>
    ),
    // Endpoint Fleet — laptop / managed device
    fleet: (
        <>
            <rect x="3" y="4" width="18" height="12" rx="1" />
            <line x1="2" y1="20" x2="22" y2="20" />
        </>
    ),
    // Identity Link Reviews — link
    link: (
        <>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </>
    ),
    // Vendor Enterprise Coverage — building
    building: (
        <>
            <rect x="4" y="2" width="16" height="20" rx="1" />
            <line x1="9" y1="6" x2="9.01" y2="6" />
            <line x1="15" y1="6" x2="15.01" y2="6" />
            <line x1="9" y1="10" x2="9.01" y2="10" />
            <line x1="15" y1="10" x2="15.01" y2="10" />
            <line x1="9" y1="14" x2="9.01" y2="14" />
            <line x1="15" y1="14" x2="15.01" y2="14" />
            <path d="M9 22v-4h6v4" />
        </>
    ),
    // Agent Installation — download
    install: (
        <>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </>
    ),
    // Bulk Upload — inverse of `install`, arrow into the same tray
    upload: (
        <>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
        </>
    ),
    // Audit Log — history / activity trail
    history: (
        <>
            <path d="M3 3v5h5" />
            <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
            <polyline points="12 7 12 12 15 14" />
        </>
    ),
    // Chevrons — used for reorder controls
    chevron_up: <polyline points="18 15 12 9 6 15" />,
    chevron_down: <polyline points="6 9 12 15 18 9" />,
    // Delete — trash can
    trash: (
        <>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </>
    ),
    // Selected — circle with a check
    check_circle: (
        <>
            <circle cx="12" cy="12" r="10" />
            <polyline points="8 12 11 15 16 9" />
        </>
    ),
    // Unselected — empty circle
    circle: <circle cx="12" cy="12" r="9" />,
};

export default function ModuleIcon({ name, size = 32, color = '#5b91f5', className = '', style = {} }) {
    const glyph = ICONS[name] || ICONS.assets;
    return (
        <svg
            className={className}
            style={style}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={`${name} icon`}
        >
            {glyph}
        </svg>
    );
}
